/**
 * Central icon registry — import icons from here, not from @hugeicons directly.
 * To swap any icon app-wide, change its mapping here.
 *
 * Named re-exports are tree-shakeable; Vite/Webpack will only bundle what's used.
 */

import type { HugeiconsIconProps } from "@hugeicons/react";

// ─── Navigation entities ─────────────────────────────────────────────────────
export { UserMultiple02Icon as LeadIcon } from "@hugeicons/core-free-icons";
export { CheckmarkSquare04Icon as TaskIcon } from "@hugeicons/core-free-icons";
export { KanbanIcon as PipelineIcon } from "@hugeicons/core-free-icons";
export { WebflowRectangleIcon as PageIcon } from "@hugeicons/core-free-icons";
export { Home04Icon as DashboardIcon } from "@hugeicons/core-free-icons";
export { InboxIcon } from "@hugeicons/core-free-icons";
export { WebflowRectangleIcon as SequenceIcon } from "@hugeicons/core-free-icons";
export { Settings01Icon as SettingsIcon } from "@hugeicons/core-free-icons";
export { Notification02Icon as NotificationIcon } from "@hugeicons/core-free-icons";
export { Menu01Icon as MenuIcon } from "@hugeicons/core-free-icons";

// ─── Actions ─────────────────────────────────────────────────────────────────
export { Delete02Icon as DeleteIcon } from "@hugeicons/core-free-icons";
export { Edit03Icon as EditIcon } from "@hugeicons/core-free-icons";
export { PlusSignIcon as AddIcon } from "@hugeicons/core-free-icons";
export { Add01Icon as AddCircleIcon } from "@hugeicons/core-free-icons";
export { Cancel01Icon as CloseIcon } from "@hugeicons/core-free-icons";
export { Upload01Icon as UploadIcon } from "@hugeicons/core-free-icons";
export { Refresh04Icon as RefreshIcon } from "@hugeicons/core-free-icons";
export { Search01Icon as SearchIcon } from "@hugeicons/core-free-icons";
export { ViewIcon as EyeIcon } from "@hugeicons/core-free-icons";
export { Link01Icon as LinkIcon } from "@hugeicons/core-free-icons";
export { ArrowReloadHorizontalFreeIcons as SyncIcon } from "@hugeicons/core-free-icons";

// ─── Navigation arrows — Lucide chevron carets (stroke-2) ────────────────────
// lucide-react isn't a dependency, so we define Lucide's exact chevron paths in
// the [tag, attrs] format HugeiconsIcon consumes. This makes every chevron on
// the platform the crisp Lucide caret (matching the Select trigger's inline
// glyphs) instead of the Hugeicons arrow.
const lucideChevron = (d: string): HugeiconsIconProps["icon"] =>
  [
    [
      "path",
      {
        d,
        stroke: "currentColor",
        strokeLinecap: "round",
        strokeLinejoin: "round",
        strokeWidth: "2",
        key: "0",
      },
    ],
  ] as HugeiconsIconProps["icon"];
export const ChevronDownIcon = lucideChevron("m6 9 6 6 6-6");
export const ChevronUpIcon = lucideChevron("m18 15-6-6-6 6");
export const ChevronRightIcon = lucideChevron("m9 18 6-6-6-6");
export const ChevronLeftIcon = lucideChevron("m15 18-6-6 6-6");

// ─── Status & feedback ───────────────────────────────────────────────────────
export { Loading03Icon as LoadingIcon } from "@hugeicons/core-free-icons";
export { Tick01Icon as CheckIcon } from "@hugeicons/core-free-icons";
export { Tick02Icon as CheckFilledIcon } from "@hugeicons/core-free-icons";
export { FlashIcon as TriggerIcon } from "@hugeicons/core-free-icons";

// ─── Content ─────────────────────────────────────────────────────────────────
export { File01Icon as FileIcon } from "@hugeicons/core-free-icons";
export { File02Icon as FileAltIcon } from "@hugeicons/core-free-icons";
export { Folder01Icon as FolderIcon } from "@hugeicons/core-free-icons";
export { Mail01Icon as MailIcon } from "@hugeicons/core-free-icons";
export { Calendar01Icon as CalendarIcon } from "@hugeicons/core-free-icons";
export { DragDropVerticalIcon as DragHandleIcon } from "@hugeicons/core-free-icons";
export { AtIcon as MentionIcon } from "@hugeicons/core-free-icons";
export { Cursor01Icon as ClickIcon } from "@hugeicons/core-free-icons";
export { TimeQuarterPassIcon as TimeIcon } from "@hugeicons/core-free-icons";
export { SidebarLeftIcon as SidebarIcon } from "@hugeicons/core-free-icons";
export { SendIcon } from "@hugeicons/core-free-icons";
